app.component("notificationAdminConsole", {
	templateUrl: "app/src/js/components/admin/notificationConsole.html",
	controller: "notificationAdminController",
	bindings: {},
});

app.controller("notificationAdminController", [
	"$scope",
	"$modal",
	"NotificationAPI",
	"AdminService",
	"$window",
	"PRACTICE_TYPE",
	"USER_ROLE",
	"NOTIFICATION_TYPE",
	"NOTIFICATION_URGENCY_TYPE",
	"NOTIFICATION_PATHWAY_TYPE",
	"NOTIFICATION_VIEW_TYPE",
	"NOTIFICATION_TEMPLATES",
	function ($scope, $modal, NotificationAPI, AdminService, $window, PRACTICE_TYPE, USER_ROLE, NOTIFICATION_TYPE, NOTIFICATION_URGENCY_TYPE, NOTIFICATION_PATHWAY_TYPE, NOTIFICATION_VIEW_TYPE, NOTIFICATION_TEMPLATES) {
		const PAGE_SIZE = 20;
		const TITLE_MAX = 50;
		const PLACEHOLDER = /\[[A-Z][A-Z ]*\]/;
		const byValue = (list, key) => list.reduce((m, o) => { m[o[key]] = o; return m; }, {});

		$scope.practiceTypeOptions = PRACTICE_TYPE;
		$scope.userRoleOptions = USER_ROLE;
		$scope.audienceOptions = NOTIFICATION_TYPE;
		$scope.urgencyOptions = NOTIFICATION_URGENCY_TYPE;
		$scope.pathwayOptions = NOTIFICATION_PATHWAY_TYPE;
		$scope.viewOptions = NOTIFICATION_VIEW_TYPE;
		$scope.templates = NOTIFICATION_TEMPLATES;
		$scope.audienceByValue = byValue(NOTIFICATION_TYPE, "value");
		$scope.urgencyByValue = byValue(NOTIFICATION_URGENCY_TYPE, "value");
		$scope.pathwayByValue = byValue(NOTIFICATION_PATHWAY_TYPE, "value");
		$scope.viewByValue = byValue(NOTIFICATION_VIEW_TYPE, "value");
		$scope.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		$scope.titleMax = TITLE_MAX;

		$scope.view = "list";
		$scope.loading = false;
		$scope.saving = false;
		$scope.notifications = [];
		$scope.userOptions = [];
		$scope.practiceOptions = [];
		$scope.filter = { status: "live", search: "" };
		$scope.limit = PAGE_SIZE;
		$scope.flash = null;

		$scope.statusLabels = { live: "Live", scheduled: "Scheduled", paused: "Paused", ended: "Ended" };
		$scope.now = () => new Date();

		$scope.activeTemplateId = null;

		// Left rail, in the same shape as the settings modal's nav.
		$scope.statusTabs = [
			{ value: "live", name: "Live", icon: "fa-circle", heading: "Live notifications", blurb: "Showing to users right now." },
			{ value: "scheduled", name: "Scheduled", icon: "fa-clock-o", heading: "Scheduled notifications", blurb: "Set to start showing at a later time." },
			{ value: "paused", name: "Paused", icon: "fa-pause", heading: "Paused notifications", blurb: "Switched off. Turn one back on and it shows again until its end date." },
			{ value: "ended", name: "Ended", icon: "fa-flag-checkered", heading: "Ended notifications", blurb: "Past their end date. Duplicate one to send it again." },
			{ value: "all", name: "All notifications", icon: "fa-list", heading: "All notifications", blurb: "Everything that has been sent or scheduled." },
		];
		$scope.statusTabByValue = byValue($scope.statusTabs, "value");

		$scope.summernoteOptions = {
			focus: false,
			height: 140,
			disableResizeEditor: true,
			inheritPlaceholder: true,
			disableDragAndDrop: true,
			dialogsInBody: true,
			toolbar: [
				["style", ["bold", "italic", "underline", "clear"]],
				["font", ["strikethrough"]],
				["para", ["ul", "ol"]],
				["insert", ["link"]],
			],
		};

		// Shared look for the four audience pickers; each adds its own id/display props.
		function multiselectSettings(extra) {
			return Object.assign({
				smartButtonMaxItems: 3,
				smartButtonTextConverter: (itemText) => itemText,
				buttonClasses: "btn btn-dropdown-multiselect",
				showCheckAll: false,
				showUncheckAll: false,
				scrollableHeight: "20em",
				scrollable: true,
			}, extra);
		}

		$scope.userOptionSettings = multiselectSettings({ idProp: "id", displayProp: "fullName", enableSearch: true });
		$scope.practiceOptionSettings = multiselectSettings({ idProp: "id", displayProp: "name", enableSearch: true });
		$scope.roleOptionSettings = multiselectSettings({});
		$scope.practiceTypeOptionSettings = multiselectSettings({ idProp: "value", displayProp: "name" });

		/* ------------------------------------------------------------------ */
		/* List                                                               */
		/* ------------------------------------------------------------------ */

		function statusOf(notification) {
			if (!notification.is_active) return "paused";
			const now = Date.now();
			if (Date.parse(notification.start_date_utc) > now) return "scheduled";
			if (Date.parse(notification.end_date_utc) < now) return "ended";
			return "live";
		}

		$scope.fetchNotifications = function () {
			$scope.loading = true;
			NotificationAPI.getAvailableNotifications().then(function (resp) {
				$scope.notifications = (resp.data || [])
					// Distinct users, in case two views of the same notification were saved at the same moment.
					.map((n) => Object.assign(n, { $status: statusOf(n), $seen: new Set(n.viewed_user_ids || []).size }))
					.sort((a, b) => Date.parse(b.date_created || 0) - Date.parse(a.date_created || 0));
			}, function () {
				showErrorDialog("Could not load notifications");
			}).finally(function () {
				$scope.loading = false;
			});
		};

		$scope.countFor = function (status) {
			return status === "all" ? $scope.notifications.length : $scope.notifications.filter((n) => n.$status === status).length;
		};

		$scope.setStatusFilter = function (status) {
			$scope.filter.status = status;
			$scope.limit = PAGE_SIZE;
			$scope.view = "list";
			$scope.activeTemplateId = null;
		};

		$scope.visibleNotifications = function () {
			const search = ($scope.filter.search || "").trim().toLowerCase();
			return $scope.notifications.filter((n) =>
				($scope.filter.status === "all" || n.$status === $scope.filter.status) &&
				(!search || (n.title || "").toLowerCase().includes(search) || stripHtml(n.message).toLowerCase().includes(search))
			);
		};

		$scope.showMore = function () {
			$scope.limit += PAGE_SIZE;
		};

		$scope.audienceSummary = function (n) {
			const names = (ids, options, idKey, nameKey) => (ids || []).map((id) => {
				const match = (options || []).find((o) => o[idKey] === id);
				return match ? match[nameKey] : id;
			});
			const listOf = (items, noun) => items.length > 2 ? items.length + " " + noun : items.join(", ");

			switch (n.notification_type) {
				case "ALL_USERS":
					return "Everyone";
				case "USER_IDS":
					return listOf(names(n.user_ids, $scope.userOptions, "id", "fullName"), "users");
				case "PRACTICE_IDS":
					return listOf(names(n.practice_ids, $scope.practiceOptions, "id", "name"), "practices");
				case "USER_ROLES":
					return listOf(names(n.user_role_types, USER_ROLE, "id", "label"), "roles");
				case "PRACTICE_TYPES":
					return listOf(names(n.practice_types, PRACTICE_TYPE, "value", "name"), "practice types");
				case "USER_ROLES_AND_PRACTICE_TYPES":
					return listOf(names(n.user_role_types, USER_ROLE, "id", "label"), "roles") + " at " +
						listOf(names(n.practice_types, PRACTICE_TYPE, "value", "name"), "practice types");
				default:
					return n.notification_type;
			}
		};

		// Bound to the row's toggle switch, so is_active has already flipped by the time this runs.
		$scope.toggleActive = function (notification) {
			notification.$busy = true;
			NotificationAPI.updateNotificationActiveStatus(notification.notification_id).then(function () {
				$scope.fetchNotifications();
			}, function () {
				notification.is_active = !notification.is_active;
				notification.$busy = false;
				showErrorDialog();
			});
		};

		/* ------------------------------------------------------------------ */
		/* Compose                                                            */
		/* ------------------------------------------------------------------ */

		function wholeMinute(date) {
			const d = new Date(date);
			d.setSeconds(0, 0);
			return d;
		}

		function blankForm() {
			const start = wholeMinute(new Date());
			return {
				title: "",
				message: "",
				notification_urgency_type: "STANDARD",
				notification_pathway_type: "BANNER",
				notification_view_type: "ONCE",
				notification_type: "ALL_USERS",
				start: start,
				end: moment(start).add(1, "week").toDate(),
			};
		}

		function resetTargets() {
			$scope.userModel = [];
			$scope.practiceModel = [];
			$scope.roleModel = [];
			$scope.practiceTypeModel = [];
		}

		$scope.newNotification = function () {
			$scope.form = blankForm();
			resetTargets();
			$scope.submitted = false;
			$scope.flash = null;
			$scope.activeTemplateId = "blank";
			$scope.view = "compose";
		};

		// Copies the content and audience of an existing notification into a fresh one. There is no
		// edit endpoint, so this is how an admin re-runs or corrects a notification.
		$scope.duplicate = function (n) {
			fillForm(n);
			$scope.activeTemplateId = null;
		};

		// Starts a new notification from one of the NOTIFICATION_TEMPLATES. Still goes through the
		// form so the admin sees the preview and audience before anything is sent.
		$scope.useTemplate = function (template) {
			fillForm(template);
			$scope.activeTemplateId = template.id;
			$scope.setDuration(template.duration[0], template.duration[1]);
		};

		function fillForm(n) {
			$scope.newNotification();
			Object.assign($scope.form, {
				title: n.title || "",
				message: n.message || "",
				notification_urgency_type: n.notification_urgency_type || "STANDARD",
				notification_pathway_type: n.notification_pathway_type || "BANNER",
				notification_view_type: n.notification_view_type || "ONCE",
				notification_type: n.notification_type || "ALL_USERS",
			});
			// ng-dropdown-multiselect keeps selections as { id } regardless of each picker's idProp.
			const asModel = (ids) => (ids || []).map((id) => ({ id: id }));
			$scope.userModel = asModel(n.user_ids);
			$scope.practiceModel = asModel(n.practice_ids);
			$scope.roleModel = asModel(n.user_role_types);
			$scope.practiceTypeModel = asModel(n.practice_types);
		}

		// What the modal preview binds to: the same shape NotificationService hands the real modal.
		// The message is a plain string, so ng-bind-html sanitises it rather than trusting it.
		$scope.$watchGroup(["form.title", "form.message", "form.notification_urgency_type"], function () {
			const f = $scope.form || {};
			$scope.notification = {
				title: f.title || "Title",
				notification_urgency_type: f.notification_urgency_type,
				trustedMessage: f.message || "Your message will appear here.",
			};
		});

		$scope.cancel = function () {
			$scope.view = "list";
			$scope.activeTemplateId = null;
		};

		$scope.needs = function (target) {
			const type = $scope.form && $scope.form.notification_type;
			switch (target) {
				case "users": return type === "USER_IDS";
				case "practices": return type === "PRACTICE_IDS";
				case "roles": return type === "USER_ROLES" || type === "USER_ROLES_AND_PRACTICE_TYPES";
				case "practiceTypes": return type === "PRACTICE_TYPES" || type === "USER_ROLES_AND_PRACTICE_TYPES";
			}
			return false;
		};

		$scope.setStartNow = function () {
			$scope.form.start = wholeMinute(new Date());
		};

		$scope.setDuration = function (amount, unit) {
			const start = isValidDate($scope.form.start) ? $scope.form.start : new Date();
			$scope.form.end = moment(start).add(amount, unit).toDate();
		};

		$scope.formErrors = function () {
			const f = $scope.form;
			const errors = [];
			if (!f) return errors;
			if (!(f.title || "").trim()) errors.push("Add a title.");
			if (!stripHtml(f.message).trim()) errors.push("Write a message.");
			if (PLACEHOLDER.test(f.title || "") || PLACEHOLDER.test(stripHtml(f.message))) errors.push("Replace the [PLACEHOLDER] text from the template.");
			if ($scope.needs("users") && !$scope.userModel.length) errors.push("Choose at least one user.");
			if ($scope.needs("practices") && !$scope.practiceModel.length) errors.push("Choose at least one practice.");
			if ($scope.needs("roles") && !$scope.roleModel.length) errors.push("Choose at least one user role.");
			if ($scope.needs("practiceTypes") && !$scope.practiceTypeModel.length) errors.push("Choose at least one practice type.");
			if (!isValidDate(f.start) || !isValidDate(f.end)) {
				errors.push("Enter a valid start and end date.");
			} else {
				if (f.end <= f.start) errors.push("The end date must be after the start date.");
				if (f.end <= new Date()) errors.push("The end date is in the past, so nobody would see it.");
			}
			return errors;
		};

		$scope.submitNotification = function () {
			$scope.submitted = true;
			if ($scope.saving || $scope.formErrors().length) {
				return;
			}
			const f = $scope.form;
			const ids = (model) => model.map((t) => t.id);
			const data = {
				title: f.title.trim(),
				message: f.message,
				notification_type: f.notification_type,
				notification_urgency_type: f.notification_urgency_type,
				notification_pathway_type: f.notification_pathway_type,
				notification_view_type: f.notification_view_type,
				timezone: $scope.timezone,
				start_date_utc: f.start.toISOString(),
				end_date_utc: f.end.toISOString(),
				// Only the targets the chosen audience uses, so a picker left filled in from a
				// different audience choice is never sent.
				user_ids: $scope.needs("users") ? ids($scope.userModel) : undefined,
				practice_ids: $scope.needs("practices") ? ids($scope.practiceModel) : undefined,
				user_role_types: $scope.needs("roles") ? ids($scope.roleModel) : undefined,
				practice_types: $scope.needs("practiceTypes") ? ids($scope.practiceTypeModel) : undefined,
			};

			$scope.saving = true;
			NotificationAPI.createNotification(data).then(function () {
				$scope.flash = f.start > new Date()
					? "“" + data.title + "” is scheduled."
					: "“" + data.title + "” is now live.";
				$scope.setStatusFilter(f.start > new Date() ? "scheduled" : "live");
				$scope.fetchNotifications();
			}, function () {
				// Keep the form open so the admin's work is not lost.
				showErrorDialog();
			}).finally(function () {
				$scope.saving = false;
			});
		};

		$scope.goBack = function () {
			$window.history.back();
		};

		/* ------------------------------------------------------------------ */
		/* Helpers & initial load                                             */
		/* ------------------------------------------------------------------ */

		function isValidDate(d) {
			return d instanceof Date && !isNaN(d.getTime());
		}

		function stripHtml(html) {
			return (html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ");
		}

		function showErrorDialog(title) {
			$scope.serverErrorModal = $modal({
				scope: $scope,
				templateUrl: "app/src/views/templates/modal.server-error.tpl.html",
				show: true,
				backdrop: "static",
				keyboard: false,
				title: title || "CatTrax has encountered an error",
			});
		}

		AdminService.getUsers().then(function (resp) {
			$scope.userOptions = (resp.data || []).map((user) => Object.assign({}, user, {
				fullName: user.firstName + " " + user.lastName,
			}));
		}, angular.noop);

		AdminService.getAvailablePractices().then(function (resp) {
			$scope.practiceOptions = resp.data || [];
		}, angular.noop);

		resetTargets();
		$scope.fetchNotifications();
	},
]);
